import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  CssBaseline,
  ThemeProvider,
  CircularProgress,
  useTheme
} from '@mui/material';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CopilotProvider, useCopilot } from './contexts/CopilotContext';
import { UploadProvider } from './contexts/UploadContext';
import type { CopilotMessage } from './contexts/CopilotContext';
import CopilotBubble from './components/CopilotBubble';
import Sidebar from './components/Sidebar';
import DashboardPage from './pages/DashboardPage';
import CertificatesPage from './pages/CertificatesPage';
import ApprovalsPage from './pages/ApprovalsPage';
import RemindersPage from './pages/RemindersPage';
import AskAIPage from './pages/AskAIPage';
import CertificateRenewalsPage from './pages/CertificateRenewalsPage';
import ProfilePage from './pages/ProfilePage';
import AccountingPage from './pages/AccountingPage';
import PropertiesPage from './pages/PropertiesPage';
import PeoplePage from './pages/PeoplePage';
import GoogleAuthCallback from './pages/GoogleAuthCallback';
import TrueLayerCallback from './pages/TrueLayerCallback';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import Login from './components/Login';
import Register from './components/Register';
import ResetPassword from './components/ResetPassword';
import PaymentSetupPage from './pages/PaymentSetupPage';
import OnboardingPage from './pages/OnboardingPage';
import AvailabilityPage from './pages/AvailabilityPage';
import AIFlowPage from './pages/AIFlowPage';
import AIFlowPage2 from './pages/AIFlowPage2';
import PricingPage from './pages/PricingPage';
import LandingPage from './pages/LandingPage';
import ComparePage from './pages/ComparePage';
import ComplianceCentrePage from './pages/ComplianceCentrePage';
import AccountingCentrePage from './pages/AccountingCentrePage';
import CookiePolicyPage from './pages/CookiePolicyPage';
import ResourcesPage from './pages/ResourcesPage';
import BlogIndexPage from './pages/BlogIndexPage';
import BlogCategoryPage from './pages/BlogCategoryPage';
import ArticlePage from './pages/ArticlePage';
import GuidesIndexPage from './pages/GuidesIndexPage';
import GuidePage from './pages/GuidePage';
import CalculatorsIndexPage from './pages/CalculatorsIndexPage';
import CalculatorPage from './pages/CalculatorPage';
import TemplatesPage from './pages/TemplatesPage';
import ChecklistsPage from './pages/ChecklistsPage';
import CaseStudiesPage from './pages/CaseStudiesPage';
import HelpPage from './pages/HelpPage';
import NewsPage from './pages/NewsPage';
import CustomersPage from './pages/CustomersPage';
import ComingSoonPage from './pages/ComingSoonPage';
import HowItWorksPage from './pages/HowItWorksPage';
import PaymentWall from './components/PaymentWall';
import LimitReachedBanner from './components/LimitReachedBanner';
import UploadStatusBar from './components/UploadStatusBar';
import UploadLimitDialog from './components/UploadLimitDialog';
import VerifyEmailGate, { RequireVerifiedEmail } from './components/VerifyEmailGate';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ComposeEmailDialog from './components/ComposeEmailDialog';
import type { ComposeEmailData } from './components/ComposeEmailDialog';
import config from './config';
import theme from './theme';
import './App.css';


// ---------------------------------------------------------------------------
// Shared layout context — passed through <Outlet context={...} /> to child routes
// ---------------------------------------------------------------------------
export interface LayoutContext {
  pendingUploadCertificates: any[];
  setPendingUploadCertificates: (certs: any[]) => void;
  uploadDialogOpen: boolean;
  setUploadDialogOpen: (open: boolean) => void;
  uploadMatchCount: number;
  setUploadMatchCount: (count: number) => void;
  hasVisitedRenewals: boolean;
  setHasVisitedRenewals: (visited: boolean) => void;
  refreshPendingRenewalsCount: () => void;
  /** Call after uploading certs or saving a provider so the copilot re-evaluates */
  refreshCopilotData: () => void;
  /** Increment to programmatically open the file picker on the dashboard */
  filePickerTrigger: number;
}

// ---------------------------------------------------------------------------
// Copilot helpers
// ---------------------------------------------------------------------------
interface CopilotCert {
  id: string;
  normalized_address: string;
  certificate_type_category: string;
}

interface CopilotProvider {
  id: string;
  provider_name: string;
  provider_email: string;
  certificate_types: string[];
}

/** "Gas Certificate, Fire Certificate" → "Gas and Fire" */
const formatTypeList = (types: string[]): string => {
  const short = types.map(t => t.replace(/ [Cc]ertificate$/, ''));
  if (short.length === 1) return short[0];
  if (short.length === 2) return `${short[0]} and ${short[1]}`;
  return short.slice(0, -1).join(', ') + ', and ' + short[short.length - 1];
};

// ---------------------------------------------------------------------------
// Loading spinner (shared)
// ---------------------------------------------------------------------------
const LoadingScreen: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <CircularProgress />
  </Box>
);

// ---------------------------------------------------------------------------
// PublicOnly — redirects authenticated users to /dashboard
// ---------------------------------------------------------------------------
const PublicOnly: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (user) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
};

// ---------------------------------------------------------------------------
// AuthenticatedLayout — sidebar + header + <Outlet />
// Redirects to landing page if not authenticated.
// ---------------------------------------------------------------------------
const AuthenticatedLayout: React.FC = () => {
  const { user, loading, token, subscription } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { palette } = useTheme();
  const { showMessage, showMessageSilent, queueMessages, clearMessage } = useCopilot();
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);

  // Persist upload dialog state across page navigation
  const [pendingUploadCertificates, setPendingUploadCertificates] = useState<any[]>([]);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadMatchCount, setUploadMatchCount] = useState(0);
  const [hasVisitedRenewals, setHasVisitedRenewals] = useState(false);
  const [pendingRenewalsCount, setPendingRenewalsCount] = useState(0);
  const [composeEmailOpen, setComposeEmailOpen] = useState(false);
  const [composeEmailData, setComposeEmailData] = useState<ComposeEmailData | null>(null);
  const [filePickerTrigger, setFilePickerTrigger] = useState(0);

  // ── Copilot monitoring state ──────────────────────────────────────────────
  const [copilotCerts, setCopilotCerts] = useState<CopilotCert[]>([]);
  const [copilotProviders, setCopilotProviders] = useState<CopilotProvider[]>([]);
  const [coveredTypes, setCoveredTypes] = useState<Set<string>>(new Set());
  const [copilotGmailConnected, setCopilotGmailConnected] = useState<boolean | null>(null);
  const [copilotAgenticEnabled, setCopilotAgenticEnabled] = useState<boolean | null>(null);
  const [copilotVersion, setCopilotVersion] = useState(0);
  const prevCertIdsRef = useRef<Set<string>>(new Set());
  const copilotInitRef = useRef(false);
  const copilotLastKeyRef = useRef('');

  const fetchPendingRenewalsCount = useCallback(async () => {
    if (!user || !token) return;
    try {
      const response = await fetch(config.apiUrl + '/api/approvals/counts', {
        headers: { 'Authorization': 'Bearer ' + token }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) setPendingRenewalsCount(data.total);
      }
    } catch {}
  }, [user, token]);

  useEffect(() => {
    fetchPendingRenewalsCount();
    const interval = setInterval(fetchPendingRenewalsCount, 30000);
    return () => clearInterval(interval);
  }, [fetchPendingRenewalsCount]);

  // ── Fetch all copilot data in one shot ───────────────────────────────────
  const refreshCopilotData = useCallback(async () => {
    if (!user || !token) return;
    try {
      const [certsRes, providersRes, scopesRes, profileRes] = await Promise.all([
        fetch(`${config.apiUrl}/api/certificates`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${config.apiUrl}/api/certificate-providers`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${config.apiUrl}/api/auth/google/scopes`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${config.apiUrl}/api/profile`, { headers: { 'Authorization': `Bearer ${token}` } }),
      ]);
      const [certsData, providersData, scopesData, profileData] = await Promise.all([
        certsRes.ok ? certsRes.json() : null,
        providersRes.ok ? providersRes.json() : null,
        scopesRes.ok ? scopesRes.json() : null,
        profileRes.ok ? profileRes.json() : null,
      ]);

      const certs: CopilotCert[] = (certsData?.certificates || []).map((c: any) => ({
        id: c.id,
        normalized_address: c.normalized_address || c.property_address || '',
        certificate_type_category: c.certificate_type_category || '',
      }));

      const covered = new Set<string>();
      const providers: CopilotProvider[] = (providersData?.providers || []).map((p: any) => {
        const types: string[] = Array.isArray(p.certificate_types) ? p.certificate_types
          : (typeof p.certificate_types === 'string' ? JSON.parse(p.certificate_types) : []);
        types.forEach(t => covered.add(t));
        return { id: String(p.id), provider_name: p.provider_name || '', provider_email: p.provider_email || '', certificate_types: types };
      });

      const authed = !!(scopesData?.success && scopesData?.is_authenticated);
      const gmail = authed ? !!scopesData.services?.gmail : false;
      const agentic = profileData?.agentic_mode_enabled || false;

      setCopilotCerts(certs);
      setCopilotProviders(providers);
      setCoveredTypes(covered);
      setCopilotGmailConnected(gmail);
      setCopilotAgenticEnabled(agentic);
      localStorage.setItem('agenticMode', String(agentic));
      copilotInitRef.current = true;
      setCopilotVersion(v => v + 1);
    } catch {}
  }, [user, token]);

  // Initial load
  useEffect(() => {
    if (user && token) refreshCopilotData();
  }, [user, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Reactive copilot: 4 checks on every data refresh ────────────────────
  useEffect(() => {
    if (!copilotInitRef.current || copilotGmailConnected === null || copilotAgenticEnabled === null) return;

    const certTotal = copilotCerts.length;
    const certIds = copilotCerts.map(c => c.id);
    const certTypeCategories = Array.from(new Set(
      copilotCerts.map(c => c.certificate_type_category).filter(Boolean)
    ));

    // Check 4 — new certs added since last fetch (skip on very first load)
    const prevIds = prevCertIdsRef.current;
    const newCerts = prevIds.size > 0 ? copilotCerts.filter(c => !prevIds.has(c.id)) : [];
    prevCertIdsRef.current = new Set(certIds);

    if (newCerts.length > 0) {
      const addresses = Array.from(new Set(newCerts.map(c => c.normalized_address).filter(Boolean)));
      const newTypes = Array.from(new Set(newCerts.map(c => c.certificate_type_category).filter(Boolean)));
      const uncoveredNewTypes = newTypes.filter(t => !coveredTypes.has(t));
      const wellDone: CopilotMessage = {
        text: (
          <>
            Well done — you added <strong>{newCerts.length}</strong> new certificate{newCerts.length > 1 ? 's' : ''} for:
            {addresses.length > 0 ? (
              <Box component="ul" sx={{ m: 0, mt: 0.75, pl: 2.5 }}>
                {addresses.map(addr => (
                  <Box component="li" key={addr} sx={{ fontSize: 'inherit' }}>{addr}</Box>
                ))}
              </Box>
            ) : ' your properties.'}
          </>
        ),
        laterLabel: 'Got it',
      };

      if (uncoveredNewTypes.length > 0) {
        const typeStr = formatTypeList(uncoveredNewTypes);
        const followUp: CopilotMessage = {
          text: (<>I noticed you don't have a provider for <strong>{typeStr}</strong> — want to add one now?</>),
          action: { label: 'Add provider', onClick: () => navigate('/providers') },
          laterLabel: "I'll do it later",
          onDismiss: () => sessionStorage.setItem('copilot_setup_dismissed', `no-provider:${uncoveredNewTypes.sort().join(',')}`),
        };
        queueMessages([wellDone, followUp]);
      } else {
        showMessage(wellDone);
      }
      copilotLastKeyRef.current = `new-certs:${certTotal}`;
      return;
    }

    // Checks 1–4: stable state (priority order)
    let key = '';
    let msg: CopilotMessage | null = null;

    // Build overlap map (used in check 3)
    const typeToProvidersList = new Map<string, CopilotProvider[]>();
    copilotProviders.forEach(p => {
      p.certificate_types.forEach(t => {
        if (!typeToProvidersList.has(t)) typeToProvidersList.set(t, []);
        typeToProvidersList.get(t)!.push(p);
      });
    });
    const firstOverlap = Array.from(typeToProvidersList.entries()).find(([, ps]) => ps.length > 1);

    if (certTotal === 0) {
      // Check 1: no certs
      key = 'no-cert';
      msg = {
        text: "Upload your first certificate and I'll extract the property, type, and expiry date automatically.",
        action: { label: 'Upload Certificate', onClick: () => { navigate('/dashboard'); setFilePickerTrigger(t => t + 1); } },
        laterLabel: "I'll do it later",
        onDismiss: () => sessionStorage.setItem('copilot_setup_dismissed', 'no-cert'),
      };
    } else {
      const uncoveredTypes = certTypeCategories.filter(t => !coveredTypes.has(t));
      if (uncoveredTypes.length > 0) {
        // Check 2: cert types with no provider
        const typeStr = formatTypeList(uncoveredTypes);
        key = `no-provider:${uncoveredTypes.sort().join(',')}`;
        msg = {
          text: (<>I noticed you don't have a provider for <strong>{typeStr}</strong>. Want to add one now?</>),
          action: { label: 'Add provider', onClick: () => navigate('/providers') },
          laterLabel: "I'll do it later",
          onDismiss: () => sessionStorage.setItem('copilot_setup_dismissed', key),
        };
      } else if (firstOverlap && !sessionStorage.getItem(`copilot_overlap_ack:${firstOverlap[0]}`)) {
        // Check 3: multiple providers covering the same cert type
        const [certType, ps] = firstOverlap;
        const ackKey = `copilot_overlap_ack:${certType}`;
        key = `overlap:${certType}`;
        const label = (p: CopilotProvider) => p.provider_name || p.provider_email;
        const names = ps.map(label);
        const nameStr = names.length === 2 ? `${names[0]} and ${names[1]}` : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
        const shortType = certType.replace(' Certificate', '');
        const assignTo = async (_keep: CopilotProvider, remove: CopilotProvider) => {
          clearMessage();
          try {
            await fetch(`${config.apiUrl}/api/certificate-providers/${remove.id}`, {
              method: 'PUT',
              headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
              body: JSON.stringify({ certificate_types: remove.certificate_types.filter(ct => ct !== certType) }),
            });
            window.dispatchEvent(new CustomEvent('copilot-providers-updated'));
            refreshCopilotData();
          } catch {}
        };
        msg = {
          text: <>I noticed <strong>{nameStr}</strong> are both handling <strong>{shortType}</strong> certificates. Do you want to assign it to just one?</>,
          action: { label: `Assign to ${label(ps[0])}`, onClick: () => assignTo(ps[0], ps[1]) },
          secondaryAction: { label: `Assign to ${label(ps[1])}`, onClick: () => assignTo(ps[1], ps[0]) },
          laterLabel: "That's correct",
          onDismiss: () => sessionStorage.setItem(ackKey, '1'),
        };
      } else if (!copilotGmailConnected) {
        // Check 4: Gmail not connected
        key = 'no-gmail';
        // Mirrors GoogleAuthButton's handleGoogleAuth — navigating to the profile page alone left
        // the user having to click "Connect Google Account" a second time, so this starts the
        // same OAuth popup flow directly from the Copilot action.
        const connectGmailNow = async () => {
          navigate('/profile');
          try {
            if (!token) return;
            const response = await fetch(`${config.apiUrl}/api/auth/google/start`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify({ redirect_uri: `${window.location.origin}/auth/google/callback` }),
            });
            if (!response.ok) throw new Error(`Failed to start Google authentication: ${response.status}`);
            const data = await response.json();
            if (!data.success || !data.auth_url) throw new Error(data.error || 'Failed to get authorization URL');
            const authTab = window.open(data.auth_url, '_blank');
            if (!authTab) throw new Error('Failed to open authentication tab. Please allow popups for this site.');
            const checkAuthInterval = setInterval(async () => {
              if (authTab.closed) {
                clearInterval(checkAuthInterval);
                try {
                  await fetch(`${config.apiUrl}/api/gmail/setup-watch`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}` },
                  });
                } catch {}
                window.dispatchEvent(new CustomEvent('googleAuthSuccess'));
                refreshCopilotData();
              }
            }, 500);
            setTimeout(() => clearInterval(checkAuthInterval), 300000);
          } catch (err) {
            showMessage({ text: err instanceof Error ? err.message : 'Failed to start Google authentication.' });
          }
        };
        msg = {
          text: "Connect Gmail and I'll auto-detect new certificates the moment they land in your inbox.",
          action: { label: 'Connect Gmail', onClick: connectGmailNow },
          laterLabel: "I'll do it later",
          onDismiss: () => sessionStorage.setItem('copilot_setup_dismissed', 'no-gmail'),
        };
      }
    }

    if (!key) {
      // All 3 steps complete
      const prevKey = copilotLastKeyRef.current;
      if (prevKey !== 'all-done') {
        copilotLastKeyRef.current = 'all-done';
        if (prevKey.startsWith('no-') || prevKey.startsWith('new-') || prevKey.startsWith('overlap:')) clearMessage();

        if (!copilotAgenticEnabled) {
          // Auto-enable toggle + run agentic flow
          setCopilotAgenticEnabled(true);
          localStorage.setItem('agenticMode', 'true');
          const t = token;
          (async () => {
            try {
              await fetch(`${config.apiUrl}/api/users/settings/agentic-mode`, { method: 'POST', headers: { 'Authorization': `Bearer ${t}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ enabled: true }) });
              await fetch(`${config.apiUrl}/api/reminders/create-all`, { method: 'POST', headers: { 'Authorization': `Bearer ${t}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ reminder_days: 21 }) });
              await fetch(`${config.apiUrl}/api/reminder-emails/generate-all`, { method: 'POST', headers: { 'Authorization': `Bearer ${t}`, 'Content-Type': 'application/json' } });
              await fetch(`${config.apiUrl}/api/reminder-emails/auto-schedule-all`, { method: 'POST', headers: { 'Authorization': `Bearer ${t}`, 'Content-Type': 'application/json' } });
            } catch {}
          })();

          // Congrats — shown once per "setup completion" (sessionStorage resets when toggled off)
          if (!sessionStorage.getItem('copilot_setup_complete_notified')) {
            sessionStorage.setItem('copilot_setup_complete_notified', '1');
            (async () => {
              try {
                const [cd, pd, profileRes] = await Promise.all([
                  fetch(`${config.apiUrl}/api/certificates`, { headers: { 'Authorization': `Bearer ${t}` } }).then(r => r.ok ? r.json() : null),
                  fetch(`${config.apiUrl}/api/certificate-providers`, { headers: { 'Authorization': `Bearer ${t}` } }).then(r => r.ok ? r.json() : null),
                  fetch(`${config.apiUrl}/api/profile`, { headers: { 'Authorization': `Bearer ${t}` } }).then(r => r.ok ? r.json() : null),
                ]);
                const certs: any[] = cd?.certificates || [];
                const providers: any[] = pd?.providers || [];
                const sorted = [...certs].sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());
                const latest = sorted[0];
                const certType = latest?.certificate_type_category || latest?.certificate_type || '';
                const exp = latest?.expiry_date;
                const isExpired = exp ? new Date(exp) < new Date(new Date().toDateString()) : false;
                const expFmt = exp ? new Date(exp).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '';
                const userName = profileRes?.name || user?.name || '';

                // Find provider whose certificate_types covers this cert type
                const matchedProvider = providers.find((p: any) =>
                  Array.isArray(p.certificate_types) && p.certificate_types.includes(certType)
                ) || (providers.length > 0 ? providers[providers.length - 1] : null);
                const providerName = matchedProvider?.provider_name || matchedProvider?.provider_email || '';
                const providerEmail = matchedProvider?.provider_email || '';

                if (isExpired && certType && expFmt && providerName) {
                  // Cert is expired — urgent contact message
                  const address = latest?.property_address || latest?.normalized_address || '';
                  const emailSubject = `URGENT: ${certType} Expired — ${address}`;
                  const emailBody = `Dear Sir/Madam,\n\nOur ${certType}${address ? ` for ${address}` : ''} has already expired on ${expFmt}.\n\nWe need to arrange a new inspection as soon as possible to ensure we remain legally compliant. Could you please contact us at your earliest convenience to schedule a visit and advise your availability?\n\nKindly also let us know if any procedures or contact details have changed.\n\nThank you,\n${userName}`;
                  showMessage({
                    text: (<>Congrats — now I have all I need and I will start looking after your certificates!{' '}I can see your <strong>{certType}</strong> has already expired on <strong>{expFmt}</strong>. Would you like me to contact <strong>{providerName}</strong> and arrange a renewal as soon as possible?</>),
                    action: {
                      label: `Contact ${providerName}`,
                      onClick: () => {
                        setComposeEmailData({ certId: latest.id, to: providerEmail, subject: emailSubject, body: emailBody });
                        setComposeEmailOpen(true);
                      },
                    },
                    laterLabel: "I'll do it later",
                  });
                } else {
                  // Cert is valid — standard congrats
                  const providerNameAny = providers.length > 0 ? (providers[providers.length - 1].provider_name || '') : '';
                  showMessage({
                    text: (<>Congrats — now I have all I need and I will start looking after your certificates!{' '}{certType ? <>Since you uploaded the <strong>{certType}</strong></> : 'Since you uploaded a certificate'}{expFmt ? <> and it expires on <strong>{expFmt}</strong></> : ''},
                      {' '}I have set a reminder on your calendar for 3 weeks before that{providerNameAny ? <> and I will contact <strong>{providerNameAny}</strong> to book an appointment</> : ''}.{' '}Once they email back the new certificate I will update the dashboard for you!</>),
                    action: { label: 'How does this work?', onClick: () => navigate('/ai-flow') },
                    laterLabel: 'Got it',
                  });
                }
              } catch {
                showMessage({ text: "Congrats — now I have all I need and I will start looking after your certificates!", action: { label: 'How does this work?', onClick: () => navigate('/ai-flow') }, laterLabel: 'Got it' });
              }
            })();
          }
        }
      }

      return;
    }

    // Dedup: same key as before → don't re-pop the bubble
    const prevKey = copilotLastKeyRef.current;
    if (key === prevKey) return;
    copilotLastKeyRef.current = key;

    const dismissedKey = sessionStorage.getItem('copilot_setup_dismissed') || '';
    const keyAdvanced = prevKey !== '' && prevKey !== 'all-done' && !prevKey.startsWith('new-certs:');

    if (!keyAdvanced && key === dismissedKey) {
      showMessageSilent(msg!);
    } else {
      showMessage(msg!);
    }
  }, [copilotVersion, copilotGmailConnected, copilotAgenticEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/" replace />;
  if (user.email_verified === false) return <Navigate to="/check-email" replace state={{ next: location.pathname }} />;

  const showPaymentWall = subscription && (
    subscription.trial_expired ||
    subscription.subscription_status === 'past_due' ||
    subscription.subscription_status === 'cancelled'
  );

  const layoutContext: LayoutContext = {
    pendingUploadCertificates,
    setPendingUploadCertificates,
    uploadDialogOpen,
    setUploadDialogOpen,
    uploadMatchCount,
    setUploadMatchCount,
    hasVisitedRenewals,
    setHasVisitedRenewals,
    refreshPendingRenewalsCount: fetchPendingRenewalsCount,
    refreshCopilotData,
    filePickerTrigger,
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh', width: '100%', overflow: 'hidden' }}>
      {showPaymentWall && <PaymentWall />}
      {/* Sidebar Navigation */}
      <Sidebar
        isExpanded={isSidebarExpanded}
        onToggle={() => setIsSidebarExpanded(!isSidebarExpanded)}
        pendingRenewalsCount={pendingRenewalsCount}
      />

      {/* Main Content Area */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100vh', width: '100%', maxWidth: '100vw', overflow: 'hidden', boxSizing: 'border-box' }}>
        <LimitReachedBanner />
        <UploadStatusBar />
        <UploadLimitDialog />
        {/* Page Content */}
        {/* overflowY: 'scroll' (not 'auto') deliberately always reserves the scrollbar's width,
            even on pages short enough not to need it yet — with 'auto', content that grows past
            the viewport (e.g. expanding a building's flat list on /properties) makes the
            scrollbar appear mid-interaction, which narrows this container by the scrollbar's
            width and can reflow text in cards above (see PropertiesPage.tsx's welcome hero) —
            visibly, distractingly, while the user is mid-click on something else entirely.
            scrollbarGutter: 'stable' is the actual modern fix for the same problem on platforms
            with overlay scrollbars (macOS default) — those don't reserve layout space just from
            overflowY:'scroll' either, so without this the reflow can still happen there. Chrome/
            Edge/Firefox support it; browsers that don't (older Safari) just fall back to the
            overflowY:'scroll' behavior above, which is still strictly better than 'auto'. */}
        <Box sx={{ flex: 1, overflowY: 'scroll', overflowX: 'hidden', scrollbarGutter: 'stable', bgcolor: palette.tokens.canvas, minHeight: 0 }}>
          <Outlet context={layoutContext} />
        </Box>
      </Box>
      <CopilotBubble />
      <ComposeEmailDialog
        open={composeEmailOpen}
        onClose={() => setComposeEmailOpen(false)}
        data={composeEmailData}
        token={token}
        onSent={() => setComposeEmailOpen(false)}
      />
    </Box>
  );
};

// ---------------------------------------------------------------------------
// App routes
// ---------------------------------------------------------------------------
const AppRoutes: React.FC = () => (
  <Routes>
    {/* Public routes */}
    <Route path="/" element={<PublicOnly><LandingPage /></PublicOnly>} />
    <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
    <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
    <Route path="/reset-password" element={<ResetPassword />} />
    <Route path="/verify-email" element={<VerifyEmailPage />} />
    <Route path="/privacy" element={<PrivacyPolicyPage />} />
    <Route path="/cookies" element={<CookiePolicyPage />} />
    <Route path="/compare" element={<ComparePage />} />
    <Route path="/compliance-centre" element={<ComplianceCentrePage />} />
    <Route path="/accounting-centre" element={<AccountingCentrePage />} />
    <Route path="/resources" element={<ResourcesPage />} />
    <Route path="/blog" element={<BlogIndexPage />} />
    <Route path="/blog/category/:category" element={<BlogCategoryPage />} />
    <Route path="/blog/:slug" element={<ArticlePage />} />
    <Route path="/guides" element={<GuidesIndexPage />} />
    <Route path="/guides/:slug" element={<GuidePage />} />
    <Route path="/calculators" element={<CalculatorsIndexPage />} />
    <Route path="/calculators/:slug" element={<CalculatorPage />} />
    <Route path="/templates" element={<TemplatesPage />} />
    <Route path="/checklists" element={<ChecklistsPage />} />
    <Route path="/case-studies" element={<CaseStudiesPage />} />
    <Route path="/help" element={<HelpPage />} />
    <Route path="/news" element={<NewsPage />} />
    <Route path="/customers" element={<CustomersPage />} />
    <Route
      path="/features"
      element={<ComingSoonPage eyebrow="Features" title="Certificates & accounting, in one cockpit" description="Every MillOS feature, start to finish — the full page is coming soon." canonicalPath="/features" />}
    />
    <Route
      path="/who-we-serve"
      element={<ComingSoonPage eyebrow="Who we serve" title="You have properties. We have an AI that runs them." description="What MillOS does depends on your size. What stays the same: you don't do the work." canonicalPath="/who-we-serve" />}
    />
    <Route
      path="/book-a-call"
      element={<ComingSoonPage eyebrow="Book a call" title="Talk to us about your portfolio" description="For 15+ flats, book a demo with the MillOS team." canonicalPath="/book-a-call" />}
    />
    <Route path="/how-it-works" element={<HowItWorksPage />} />
    <Route
      path="/for/letting-agents"
      element={<ComingSoonPage eyebrow="For letting agents" title="Grow units, not headcount" description="The compliance liability lands on the agent. MillOS absorbs the chase across every managed unit." canonicalPath="/for/letting-agents" />}
    />
    <Route
      path="/for/portfolio-landlords"
      element={<ComingSoonPage eyebrow="For portfolio landlords" title="One cockpit, every property" description="MillOS runs both loops, unattended, across your whole portfolio." canonicalPath="/for/portfolio-landlords" />}
    />
    <Route path="/auth/google/callback" element={<GoogleAuthCallback />} />
    <Route path="/auth/truelayer/callback" element={<TrueLayerCallback />} />
    <Route path="/payment-setup" element={<PaymentSetupPage />} />
    <Route path="/onboarding" element={<RequireVerifiedEmail next="/onboarding"><OnboardingPage /></RequireVerifiedEmail>} />
    <Route path="/check-email" element={<VerifyEmailGate />} />
    <Route path="/availability/:token" element={<AvailabilityPage />} />
    <Route path="/pricing" element={<PricingPage />} />

    {/* Authenticated routes (share sidebar + header layout) */}
    <Route element={<AuthenticatedLayout />}>
      <Route path="/dashboard" element={<DashboardPage />} />
      <Route path="/recentactivity" element={<CertificatesPage />} />
      <Route path="/properties" element={<PropertiesPage />} />
      <Route path="/people" element={<PeoplePage />} />
      <Route path="/profiles" element={<Navigate to="/people" replace />} />
      <Route path="/accounting" element={<AccountingPage />} />
      <Route path="/copilot" element={<RemindersPage />} />
      <Route path="/reminders" element={<Navigate to="/copilot" replace />} />
      <Route path="/providers" element={<CertificateRenewalsPage />} />
      <Route path="/approvals" element={<ApprovalsPage />} />
      <Route path="/integrations" element={<Navigate to="/profile" replace />} />
      <Route path="/ask-ai" element={<AskAIPage />} />
      <Route path="/profile" element={<ProfilePage />} />
      <Route path="/ai-flow" element={<AIFlowPage />} />
      <Route path="/ai-flow2" element={<AIFlowPage2 />} />
    </Route>

    {/* Catch-all */}
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
);

// ---------------------------------------------------------------------------
// Root App
// ---------------------------------------------------------------------------
function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <CopilotProvider>
        <BrowserRouter>
          <AuthProvider>
            <UploadProvider>
              <AppRoutes />
            </UploadProvider>
          </AuthProvider>
        </BrowserRouter>
      </CopilotProvider>
    </ThemeProvider>
  );
}

export default App;
